import React from 'react';
import { Image, Placeholder, Text } from '@sitecore-jss/sitecore-jss-react';
import PropTypes from 'prop-types';
import ReplaceableText from '../../../utils/text/ReplaceableText';
import ReplaceableRichText from '../../../utils/text/ReplaceableRichText';

const SITECORE_BLANK_DATE = '0001-01-01T00:00:00Z';

const CrossSellBar = ({ rendering, fields }) => {
  const { displayStartDate, displayEndDate } = fields;
  const now = new Date();

  if (
    !displayStartDate?.value ||
    displayStartDate.value === SITECORE_BLANK_DATE ||
    !displayEndDate?.value ||
    displayEndDate.value === SITECORE_BLANK_DATE
  ) {
    return null;
  }

  const startDate = new Date(displayStartDate.value);
  if (now < startDate) {
    return null;
  }

  const endDate = new Date(displayEndDate.value);
  if (now > endDate) {
    return null;
  }

  return renderComponent(fields, rendering);
};

const renderComponent = (fields, rendering) => {
  return (
    <div className="vds-cross-sell-bar vds-cross-sell-bar--default">
      <div className="vds-cross-sell-bar__inner">
        {fields.image && (
          <div className="vds-cross-sell-bar__image-container">
            <Image
              field={fields.image}
              className="vds-cross-sell-bar__image"
              data-testid={`${fields.testid?.value}-image`}
            />
          </div>
        )}
        <div className="vds-cross-sell-bar__body">
          <div className="vds-cross-sell-bar__content">
            <div className="vds-cross-sell-bar__title-container">
              <ReplaceableText
                tag="h5"
                className="vds-cross-sell-bar__pretitle"
                field={fields.tag}
                data-testid={`${fields.testid?.value}-tag`}
              />
              <ReplaceableRichText
                tag="h2"
                className="vds-cross-sell-bar__title"
                field={fields.title}
                data-testid={`${fields.testid?.value}-title`}
                isCurrency={fields.titleFormatsCurrency?.value}
              />
              <ReplaceableRichText
                tag="p"
                className="vds-cross-sell-bar__quote-secondary"
                field={fields.secondaryTitle}
                data-testid={`${fields.testid?.value}-secondaryTitle`}
                isCurrency={fields.secondaryTitleFormatsCurrency?.value}
              />
              <ReplaceableRichText
                tag="p"
                className="vds-cross-sell-bar__legal-text"
                field={fields.legalText}
                data-testid={`${fields.testid?.value}-legalText`}
              />
            </div>
            {fields.message?.value && (
              <div className="vds-cross-sell-bar__message">
                <div className="vds-cross-sell-bar__message-main">
                  <ReplaceableRichText
                    field={fields.message}
                    data-testid={`${fields.testid?.value}-message`}
                    isCurrency={fields.messageFormatsCurrency?.value}
                  />
                </div>
              </div>
            )}
            <Placeholder name="crossSellBar-content" rendering={rendering} />
          </div>
          {(fields.primaryCtaLink?.value || fields.secondaryCtaLink?.value) && (
            <div className="vds-cross-sell-bar__cta-container vds-cta-block">
              <div className="vds-cross-sell-bar__cta-item">
                {fields.primaryCtaLink?.value && fields.primaryCtaText?.value && (
                  <a
                    className="vds-cross-sell-bar__cta vds-cta-block__button vds-button"
                    href={fields.primaryCtaLink?.value}
                    data-testid={`${fields.testid?.value}-primaryCta`}
                  >
                    <Text field={fields.primaryCtaText} />
                  </a>
                )}
              </div>
              <div className="vds-cross-sell-bar__cta-item">
                {fields.secondaryCtaLink?.value && fields.secondaryCtaText?.value && (
                  <a
                    className="vds-cross-sell-bar__cta vds-cta-block__button vds-button vds-button--line"
                    href={fields.secondaryCtaLink?.value}
                    data-testid={`${fields.testid?.value}-secondaryCta`}
                  >
                    <Text field={fields.secondaryCtaText} />
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

CrossSellBar.propTypes = {
  rendering: PropTypes.shape({
    placeholders: PropTypes.object,
  }),
  fields: PropTypes.shape({
    testid: PropTypes.shape({
      value: PropTypes.string,
    }),
    image: PropTypes.shape({
      value: PropTypes.shape({
        src: PropTypes.string,
      }),
    }),
    tag: PropTypes.shape({
      value: PropTypes.string,
    }),
    title: PropTypes.shape({
      value: PropTypes.string,
    }),
    titleFormatsCurrency: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    secondaryTitle: PropTypes.shape({
      value: PropTypes.string,
    }),
    secondaryTitleFormatsCurrency: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    legalText: PropTypes.shape({
      value: PropTypes.string,
    }),
    message: PropTypes.shape({
      value: PropTypes.string,
    }),
    messageFormatsCurrency: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    primaryCtaLink: PropTypes.shape({
      value: PropTypes.string,
    }),
    primaryCtaText: PropTypes.shape({
      value: PropTypes.string,
    }),
    secondaryCtaLink: PropTypes.shape({
      value: PropTypes.string,
    }),
    secondaryCtaText: PropTypes.shape({
      value: PropTypes.string,
    }),
    displayStartDate: PropTypes.shape({
      value: PropTypes.string,
    }),
    displayEndDate: PropTypes.shape({
      value: PropTypes.string,
    }),
  }).isRequired,
};

export default CrossSellBar;
